import React, { useState, useEffect, useRef } from 'react';
import { Music, X, Play, SkipForward, Trash2, Loader2, Search, Youtube } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { getToken, clearToken } from '../../utils/tokenStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth-aware fetch for DJ Queue
const djAuthFetch = async (url, options = {}) => {
  const token = getToken();
  if (!token) {
    toast.error('Please log in to continue');
    return null;
  }
  const res = await fetch(url, {
    ...options,
    headers: { 'Authorization': `Bearer ${token}`, ...(options.headers || {}) },
  });
  if (res.status === 401) {
    clearToken();
    toast.error('Session expired — please log in again');
    return null;
  }
  return res;
};

const DJQueue = ({ isOpen, onClose, user, isGuest, isAdmin, socketRef }) => {
  const [queue, setQueue] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [joining, setJoining] = useState(false);
  const searchTimeout = useRef(null);

  useEffect(() => {
    if (isOpen) fetchQueue();
  }, [isOpen]);

  useEffect(() => {
    if (socketRef?.current) {
      socketRef.current.on('dj_queue_updated', () => fetchQueue());
      return () => socketRef.current.off('dj_queue_updated');
    }
  }, [socketRef]);

  const fetchQueue = async () => {
    try {
      const res = await fetch(`${API_URL}/api/nightclub/dj-queue`);
      if (res.ok) setQueue(await res.json());
    } catch (e) { /* silent */ }
  };

  const searchYouTube = async (query) => {
    if (!query || query.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/api/nightclub/youtube-search?q=${encodeURIComponent(query)}`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data);
      }
    } catch (e) {
      console.error('YouTube search failed:', e);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchInput = (value) => {
    setSearchQuery(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchYouTube(value), 500);
  };

  const addToQueue = async (video) => {
    if (isGuest) { toast.error('Sign in to join the DJ queue'); return; }
    setJoining(true);
    try {
      const res = await djAuthFetch(`${API_URL}/api/nightclub/dj-queue/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          song_title: video.title,
          genre: video.channel,
          youtube_url: video.url,
          video_id: video.video_id,
          thumbnail: video.thumbnail
        })
      });
      if (!res) { setJoining(false); return; }
      if (res.ok) {
        toast.success(`Added "${video.title}" to queue!`);
        setSearchQuery('');
        setSearchResults([]);
        fetchQueue();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to join queue');
      }
    } catch (e) {
      toast.error('Failed to add to queue');
    } finally {
      setJoining(false);
    }
  };

  const playEntry = async (queueId) => {
    try {
      const res = await djAuthFetch(`${API_URL}/api/nightclub/dj-queue/${queueId}/play`, {
        method: 'PUT'
      });
      if (!res) return;
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchQueue();
      }
    } catch (e) {
      toast.error('Failed to play');
    }
  };

  const updateStatus = async (queueId, status) => {
    try {
      await djAuthFetch(`${API_URL}/api/nightclub/dj-queue/${queueId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      fetchQueue();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  if (!isOpen) return null;

  const currentDJ = queue.find(q => q.status === 'playing');
  const waiting = queue.filter(q => q.status === 'waiting');

  return (
    <div className="absolute top-16 left-4 z-40 w-80" data-testid="dj-queue-panel">
      <div className="glass-heavy rounded-xl border border-white/10 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Music className="w-4 h-4 text-fuchsia-400" />
            <span className="font-semibold text-sm">DJ Queue</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-500/20 text-fuchsia-300">{waiting.length} waiting</span>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1" data-testid="close-dj-queue">
            <X size={16} />
          </button>
        </div>

        {/* Now Playing */}
        {currentDJ && (
          <div className="p-3 bg-fuchsia-500/10 border-b border-white/10">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-fuchsia-400 animate-pulse" />
              <span className="text-xs text-fuchsia-300 font-semibold uppercase tracking-wider">Now Playing</span>
            </div>
            <div className="flex items-center gap-2">
              {currentDJ.thumbnail && (
                <img src={currentDJ.thumbnail} alt="" className="w-10 h-10 rounded object-cover" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate">{currentDJ.song_title || currentDJ.user_name}</p>
                <p className="text-xs text-white/50">{currentDJ.user_name}</p>
              </div>
            </div>
            {isAdmin && (
              <Button size="sm" onClick={() => updateStatus(currentDJ.queue_id, 'done')}
                className="mt-2 h-7 text-xs bg-white/10 hover:bg-white/20" data-testid="skip-current-btn">
                <SkipForward size={12} className="mr-1" /> Next
              </Button>
            )}
          </div>
        )}

        {/* Queue List */}
        <div className="max-h-40 overflow-y-auto">
          {waiting.length === 0 ? (
            <p className="text-white/40 text-xs text-center py-3">Queue is empty - search a song below!</p>
          ) : waiting.map((entry, idx) => (
            <div key={entry.queue_id} className="flex items-center gap-2 p-2 border-b border-white/5 last:border-0" data-testid={`queue-entry-${entry.queue_id}`}>
              <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                {idx + 1}
              </span>
              {entry.thumbnail && (
                <img src={entry.thumbnail} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{entry.song_title || 'Untitled'}</p>
                <p className="text-[10px] text-white/40 truncate">{entry.user_name}</p>
              </div>
              {isAdmin && (
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => playEntry(entry.queue_id)} className="text-green-400 hover:text-green-300" title="Play now" data-testid={`play-entry-${entry.queue_id}`}>
                    <Play size={12} />
                  </button>
                  <button onClick={() => updateStatus(entry.queue_id, 'skipped')} className="text-red-400 hover:text-red-300" title="Remove">
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* YouTube Search */}
        {!isGuest && (
          <div className="p-3 border-t border-white/10 space-y-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
              <Input
                value={searchQuery}
                onChange={(e) => handleSearchInput(e.target.value)}
                placeholder="Search for a song..."
                className="bg-white/5 border-white/10 text-xs h-8 pl-8"
                maxLength={100}
                data-testid="dj-queue-search-input"
              />
              {searching && (
                <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-fuchsia-400 animate-spin" />
              )}
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="max-h-44 overflow-y-auto rounded-lg border border-white/10 bg-black/40" data-testid="youtube-search-results">
                {searchResults.map((video) => (
                  <button
                    key={video.video_id}
                    onClick={() => addToQueue(video)}
                    disabled={joining}
                    className="w-full flex items-center gap-2 p-2 hover:bg-white/10 transition-colors text-left border-b border-white/5 last:border-0"
                    data-testid={`search-result-${video.video_id}`}
                  >
                    {video.thumbnail && (
                      <img src={video.thumbnail} alt="" className="w-12 h-9 rounded object-cover flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-white truncate">{video.title}</p>
                      <div className="flex items-center gap-1.5 text-[10px] text-white/40">
                        <Youtube className="w-3 h-3 text-red-400" />
                        <span className="truncate">{video.channel}</span>
                        {video.duration && <span>&#183; {video.duration}</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DJQueue;
